import { OfferPaymentType } from "@1games-ru/region-shared/dist/enums/offer/offer";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider.ts";
import { OfferMpProvider } from "@/core/data-provider/data-mp/offer/offer-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { clearOffer, offerResolverSelector } from "@/domains/offer/slice";
import toast from "@/domains/toasts/helpers/toast";

export default () => {
  const { t } = useTranslation("offer");
  const dispatch = useAppDispatch();
  const offerResolver = useAppSelector(offerResolverSelector);
  const [pending, setPending] = useState<boolean>(false);

  const close = useCallback(() => {
    dispatch(clearOffer());
    GameMpProvider.closeCurrentInterface(true);
  }, [dispatch]);

  const resolve = useCallback(
    async (paymentType: OfferPaymentType) => {
      try {
        setPending(true);
        const response = await OfferMpProvider.resolve(offerResolver.uuid, true, paymentType);

        if (response instanceof RpcMpProviderFailedResponse) {
          console.error("Failed to fetch offerResol", response);
          toast.error(t("offer.toast.resolveError"));
        }

        if (response instanceof RpcMpProviderSuccessResponse) {
          toast.success(t("offer.toast.resolveSuccess"));
        }

        close();
      } catch (error) {
        console.error("Error resolving offer:", error);
      } finally {
        setPending(false);
      }
    },
    [offerResolver, close, t],
  );

  const resolveByCash = useCallback(() => {
    return resolve(OfferPaymentType.Cash);
  }, [resolve]);

  const resolveByCard = useCallback(() => {
    return resolve(OfferPaymentType.Card);
  }, [resolve]);

  const resolveAccept = useCallback(() => {
    return resolve(OfferPaymentType.NotProvided);
  }, [resolve]);

  const reject = useCallback(async () => {
    try {
      setPending(true);
      await OfferMpProvider.resolve(offerResolver.uuid, false);
      close();
    } catch (error) {
      console.error("Error rejecting offer:", error);
    } finally {
      setPending(false);
    }
  }, [offerResolver, close]);

  useEffect(() => {
    let timer = null;

    if (offerResolver && offerResolver.uuid) {
      OfferMpProvider.keep(offerResolver.uuid, 600);
      timer = setInterval(() => {
        OfferMpProvider.keep(offerResolver.uuid, 600);
      }, 500000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [offerResolver]);

  return {
    resolveByCash,
    resolveByCard,
    resolveAccept,
    reject,
    pending,
  };
};
